import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function AboutContentManager() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content/about").then((res) => setContent(res.data)); }, []);

  if (!content) return <p className="text-muted-foreground text-sm">Loading…</p>;

  const updateChapter = (i, field, value) => {
    const chapters = [...content.chapters];
    chapters[i] = { ...chapters[i], [field]: value };
    setContent({ ...content, chapters });
  };
  const addChapter = () => setContent({ ...content, chapters: [...content.chapters, { num: String(content.chapters.length + 1).padStart(2, "0"), title: "", text: "" }] });
  const removeChapter = (i) => setContent({ ...content, chapters: content.chapters.filter((_, idx) => idx !== i) });

  const updateFeature = (i, field, value) => {
    const features = [...content.features];
    features[i] = { ...features[i], [field]: value };
    setContent({ ...content, features });
  };
  const addFeature = () => setContent({ ...content, features: [...content.features, { title: "", desc: "" }] });
  const removeFeature = (i) => setContent({ ...content, features: content.features.filter((_, idx) => idx !== i) });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/about", content);
      toast.success("About page updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="about-content-manager" className="bg-background border border-border p-6 space-y-8">
      <div className="space-y-4">
        <div><Label>Eyebrow</Label><Input data-testid="about-form-eyebrow-input" className="rounded-none mt-1.5" value={content.eyebrow} onChange={(e) => setContent({ ...content, eyebrow: e.target.value })} /></div>
        <div><Label>Title</Label><Input data-testid="about-form-title-input" className="rounded-none mt-1.5" value={content.title} onChange={(e) => setContent({ ...content, title: e.target.value })} /></div>
        <div><Label>Subtitle</Label><Textarea data-testid="about-form-subtitle-input" className="rounded-none mt-1.5" value={content.subtitle} onChange={(e) => setContent({ ...content, subtitle: e.target.value })} /></div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-heading text-lg">Manifesto Chapters</h4>
          <Button size="sm" variant="outline" className="rounded-none" data-testid="about-add-chapter-button" onClick={addChapter}><Plus className="h-4 w-4 mr-1" /> Add Chapter</Button>
        </div>
        <div className="space-y-4">
          {content.chapters.map((c, i) => (
            <div key={i} data-testid={`about-chapter-row-${i}`} className="border border-border p-4 space-y-2">
              <div className="flex gap-2">
                <Input data-testid={`about-chapter-num-${i}`} className="rounded-none w-20" value={c.num} onChange={(e) => updateChapter(i, "num", e.target.value)} />
                <Input data-testid={`about-chapter-title-${i}`} className="rounded-none flex-1" value={c.title} onChange={(e) => updateChapter(i, "title", e.target.value)} />
                <Button size="icon" variant="outline" className="rounded-none shrink-0" data-testid={`about-chapter-delete-${i}`} onClick={() => removeChapter(i)}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <Textarea data-testid={`about-chapter-text-${i}`} className="rounded-none" value={c.text} onChange={(e) => updateChapter(i, "text", e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-heading text-lg">Feature Cards</h4>
          <Button size="sm" variant="outline" className="rounded-none" data-testid="about-add-feature-button" onClick={addFeature}><Plus className="h-4 w-4 mr-1" /> Add Feature</Button>
        </div>
        <div className="space-y-4">
          {content.features.map((f, i) => (
            <div key={i} data-testid={`about-feature-row-${i}`} className="border border-border p-4 space-y-2">
              <div className="flex gap-2">
                <Input data-testid={`about-feature-title-${i}`} className="rounded-none flex-1" value={f.title} onChange={(e) => updateFeature(i, "title", e.target.value)} />
                <Button size="icon" variant="outline" className="rounded-none shrink-0" data-testid={`about-feature-delete-${i}`} onClick={() => removeFeature(i)}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <Input data-testid={`about-feature-desc-${i}`} className="rounded-none" value={f.desc} onChange={(e) => updateFeature(i, "desc", e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} data-testid="about-content-save-button" className="w-full rounded-none bg-accent text-accent-foreground">
        {saving ? "Saving…" : "Save About Page"}
      </Button>
    </div>
  );
}
